import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { PSRecord } from '../../models/PSRecord'
import { RecordDataMap } from '../../models/RecordDataMap'
import { RecordFactory } from '../../models/RecordFactory'
import { User } from '../../models/User'
import { PocketComponentProps, usePSComponent } from '../PocketComponent'
import { Spinner } from '../Spinner'
import Button from '../app/design/Button'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import SegmentedControl from '../app/design/SegmentedControl'
import { ClientSearchableSelect } from '../contacts/ClientSearchableSelect'
import { ContractSearchableSelect } from './ContractSearchableSelect'
import { TypeSearchableSelect } from './TypeSearchableSelect'
import { PDFPreviewModal } from '../common/PDFPreviewModal'
import { Chip } from '../common/fields/Chip'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { CustomFieldRecordModal } from './CustomFieldRecordModal'

const render = ({ base: { owner, logAndDisplayError } }: PocketComponentProps<void>) => {
    const navigate = useNavigate()

    const [record, setRecord] = useState<PSRecord>(new PSRecord({ channel: 'sms' }))
    const [formCompleter, setFormCompleter] = useState<'client' | 'myself'>('client')
    const [isContractPreviewModalOpen, setIsContractPreviewModalOpen] = useState(false)
    const [isCustomFieldsEditorModalOpen, setIsCustomFieldsEditorModalOpen] = useState(false)
    const [answers, setAnswers] = useState<Record<string, any>>({})

    const [isLoading, setIsLoading] = useState(false)

    const isFormFilledOut = Object.keys(answers).length > 0
    const canSend =
        record.person !== undefined &&
        record.type !== undefined &&
        (formCompleter === 'myself' || !record.type.isPublic ? isFormFilledOut : true)

    const { data: fields } = useQuery({
        queryKey: ['form-fields', record.type?.id],
        queryFn: () => {
            return record.type?.id !== undefined
                ? RecordFactory.getInstance().loadRecordFields(record.type.id ?? '', false)
                : []
        },
    })

    const handleSelectClient = (client: User | undefined) => {
        const updatedRecord = record.clone({ person: client })

        if (client !== undefined) {
            if (!client.phone && record.channel === 'sms') {
                updatedRecord.channel = 'email'
            }
            else if (!client.email && record.channel === 'email') {
                updatedRecord.channel = 'sms'
            }
        }

        setRecord(updatedRecord)
    }

    const handleSend = async (shouldNotify: boolean) => {
        if (!canSend) return

        record.name = record.type?.name
        record.fieldValues = new RecordDataMap(answers)
        record.employee = owner.employee
        record.owner = record.type?.owner ?? owner

        setIsLoading(true)
        const result = await RecordFactory.getInstance().sendRecord(
            record,
            false,
            !shouldNotify || !record.type?.isPublic
        )
        setIsLoading(false)

        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        navigate('/dashboards/records')
    }

    return (
        <WorkflowPage
            title="Send form"
            onClose={{
                action: 'go-back',
                excludePathname: /^\/forms\/send/,
                defaultPathname: '/dashboards/records',
            }}
            footerContent={
                <div className="flex w-full items-center justify-end gap-x-4">
                    <Button
                        intent="secondary"
                        disabled={!canSend || formCompleter === 'client'}
                        onClick={() => handleSend(false)}
                        isLoading={isLoading}
                    >
                        Save
                    </Button>

                    <Button
                        intent="primary"
                        disabled={!canSend}
                        onClick={() => handleSend(true)}
                        isLoading={isLoading}
                    >
                        Send
                    </Button>
                </div>
            }
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start gap-y-6">
                <WorkflowPage.Card title="General">
                    <WorkflowPage.IconSection title="Client (required)" icon="user.large">
                        <ClientSearchableSelect
                            mode="server"
                            selectedClient={record.person}
                            selectedSubaccount={record.subaccount}
                            onSelectClient={handleSelectClient}
                            onSelectSubaccount={(subaccount) =>
                                setRecord(record.clone({ person: subaccount?.client, subaccount }))
                            }
                            shouldPreserveLastSelection={true}
                            shouldIncludeSubaccounts
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Form (required)" icon="field.large">
                        <TypeSearchableSelect
                            owner={owner}
                            selectedForm={record.type}
                            onSelectForm={(form) => setRecord(record.clone({ type: form }))}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Send via"
                        icon="messageArrowUp.large"
                        description="Specify how you want this form to be sent"
                    >
                        <div className="flex flex-wrap items-center justify-start gap-x-2 gap-y-1">
                            <Chip
                                title="Text message"
                                isSelected={record.channel === 'sms'}
                                onClick={() => setRecord(record.clone({ channel: 'sms' }))}
                                size="small"
                            />
                            <Chip
                                title="Text and email"
                                isSelected={record.channel === 'combo'}
                                onClick={() => setRecord(record.clone({ channel: 'combo' }))}
                                size="small"
                            />
                            <Chip
                                title="Email"
                                isSelected={record.channel === 'email'}
                                onClick={() => setRecord(record.clone({ channel: 'email' }))}
                                size="small"
                            />
                        </div>
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Attach a contract" icon="pen.large">
                        <ContractSearchableSelect
                            selectedContract={record.contract}
                            onSelectContract={(contract) => setRecord(record.clone({ contract }))}
                        />

                        {record.contract !== undefined && (
                            <div className="flex items-center justify-start">
                                <Button
                                    intent="tertiaryLink"
                                    onClick={() => setIsContractPreviewModalOpen(true)}
                                    className="px-0"
                                >
                                    Preview contract
                                </Button>
                            </div>
                        )}
                    </WorkflowPage.IconSection>

                    {record.type === undefined || record.type.isPublic ? (
                        <WorkflowPage.IconSection
                            title="Who should complete this form?"
                            icon="usersDocument.large"
                            description="Select whether you'd like to send this form to the client or fill it out yourself. Sending a form to the client will notify them and allow them to fill out the form."
                        >
                            <SegmentedControl
                                segments={[
                                    { label: 'Client', value: 'client' },
                                    { label: 'Myself on behalf of client', value: 'myself' },
                                ]}
                                value={formCompleter}
                                onChange={(value) =>
                                    setFormCompleter(value as typeof formCompleter)
                                }
                                size="medium"
                                className="w-full"
                            />

                            {formCompleter === 'myself' && (
                                <>
                                    <div
                                        className="relative mt-8 flex cursor-pointer items-center justify-between before:absolute before:inset-[-16px] before:-z-10 before:hidden before:rounded-lg before:bg-black/4 before:content-[''] hover:before:block"
                                        onClick={
                                            record.type !== undefined
                                                ? () => setIsCustomFieldsEditorModalOpen(true)
                                                : undefined
                                        }
                                    >
                                        <div className="flex flex-col items-start justify-start gap-y-1">
                                            <Label>Client fields</Label>
                                            <Paragraph size="sm" className="text-gray-600">
                                                Fill out fields on behalf of the client
                                            </Paragraph>
                                            {Object.keys(answers).length > 0 && (
                                                <Paragraph size="sm" className="text-secondary-400">
                                                    {Object.keys(answers).length}{' '}
                                                    {Object.keys(answers).length === 1
                                                        ? 'field'
                                                        : 'fields'}{' '}
                                                    filled out
                                                </Paragraph>
                                            )}
                                        </div>

                                        {fields !== undefined ? (
                                            <Button
                                                intent="tertiary_deprecated"
                                                className="row-span-2 whitespace-nowrap hover:bg-transparent"
                                                disabled={record.type === undefined}
                                            >
                                                Fill out
                                            </Button>
                                        ) : (
                                            <Spinner size={32} />
                                        )}
                                    </div>
                                </>
                            )}
                        </WorkflowPage.IconSection>
                    ) : (
                        <WorkflowPage.IconSection
                            title="Complete form"
                            icon="usersDocument.large"
                            hasHoverEffect
                            secondaryView={
                                fields !== undefined ? (
                                    <Button
                                        intent="tertiary_deprecated"
                                        className="row-span-2 whitespace-nowrap hover:bg-transparent"
                                        disabled={record.type === undefined}
                                        onClick={
                                            record.type !== undefined
                                                ? () => setIsCustomFieldsEditorModalOpen(true)
                                                : undefined
                                        }
                                    >
                                        Fill out
                                    </Button>
                                ) : (
                                    <Spinner size={32} />
                                )
                            }
                        />
                    )}
                </WorkflowPage.Card>
            </div>

            <PDFPreviewModal
                isOpen={isContractPreviewModalOpen}
                onClose={() => setIsContractPreviewModalOpen(false)}
                endpoint={
                    record.contract !== undefined
                        ? { path: `/contract/${record.contract.id}/print` }
                        : undefined
                }
            />

            <CustomFieldRecordModal
                isOpen={isCustomFieldsEditorModalOpen}
                onClose={() => setIsCustomFieldsEditorModalOpen(false)}
                title="Client fields"
                fields={fields ?? []}
                answers={answers}
                onUpdateAnswers={setAnswers}
                shouldEnforceRequired
            />
        </WorkflowPage>
    )
}

export const NewSendRecordPage = usePSComponent(render)
